import {
  FC,
  useState,
  useEffect,
  createContext,
  useContext,
  useRef,
  Dispatch,
  SetStateAction,
} from 'react'
import {LayoutSplashScreen} from '../../../../_metronic/layout/core'
import {AuthModel, UserModel} from './_models'
import * as authHelper from './AuthHelpers'
import {getUserByToken} from './_requests'
import {WithChildren} from '../../../../_metronic/helpers'

type AuthContextProps = {
  auth: AuthModel | undefined
  saveAuth: (auth: AuthModel | undefined) => void
  currentUser: UserModel | undefined
  setCurrentUser: Dispatch<SetStateAction<UserModel | undefined>>
  logout: () => void
  isAdmin: boolean
  isPartner: boolean
  isFranchise: boolean
  isAuditor: boolean
  isSponsor: boolean
  isSupplyChainManager: boolean
  isFacilityManager: boolean
  setisAdmin: any
  setisSponsor: any
  setisPartner: any
  setisFranchise: any
  setisAuditor: any
  setisSupplyChainManager: any
  setisFacilityManager: any
}

const initAuthContextPropsState = {
  auth: authHelper.getAuth(),
  saveAuth: () => {},
  currentUser: undefined,
  setCurrentUser: () => {},
  logout: () => {},
  isAdmin: false,
  isPartner: false,
  isFranchise: false,
  isAuditor: false,
  isSponsor: false,
  isSupplyChainManager: false,
  isFacilityManager: false,
  setisAdmin: () => {},
  setisAuditor: () => {},
  setisPartner: () => {},
  setisSponsor: () => {},
  setisFranchise: () => {},
  setisSupplyChainManager: () => {},
  setisFacilityManager: () => {},
}

const AuthContext = createContext<AuthContextProps>(initAuthContextPropsState)

const useAuth = () => {
  return useContext(AuthContext)
}

const AuthProvider: FC<WithChildren> = ({children}) => {
  const [auth, setAuth] = useState<AuthModel | undefined>(authHelper.getAuth())
  const [isSponsor, setisSponsor] = useState(false)
  const [isAdmin, setisAdmin] = useState<any>(false)
  const [isSupplyChainManager, setisSupplyChainManager] = useState<any>(false)
  const [isFacilityManager, setisFacilityManager] = useState<any>(false)
  const [isPartner, setisPartner] = useState<any>(false)
  const [isFranchise, setisFranchise] = useState<any>(false)
  const [isAuditor, setisAuditor] = useState<any>(false)
  const [currentUser, setCurrentUser] = useState<UserModel | undefined>()
  const saveAuth = (auth: AuthModel | undefined) => {
    setAuth(auth)
    if (auth) {
      setisAdmin(auth?.data?.userType === 'ADMIN')
      setisSponsor(auth?.data?.userType === 'SPONSOR')
      setisPartner(auth?.data?.userType === 'CLIENT')
      setisFranchise(auth?.data?.userType === 'FRANCHISE')
      setisAuditor(auth?.data?.userType === 'AUDITOR')
      setisSupplyChainManager(auth?.data?.userType === 'FRANCHISE_USER')
      setisFacilityManager(auth?.data?.userType === 'PICKUP_POINT_STAFF')
      authHelper.setAuth(auth)
    } else {
      authHelper.removeAuth()
    }
  }

  const logout = () => {
    setisAdmin(false)
    setisAuditor(false)
    setisPartner(false)
    saveAuth(undefined)
    setisFranchise(false)
    setCurrentUser(undefined)
    setisSupplyChainManager(false)
    setisFacilityManager(false)
  }

  return (
    <AuthContext.Provider
      value={{
        auth,
        isAdmin,
        isSponsor,
        isPartner,
        isFranchise,
        isSupplyChainManager,
        isFacilityManager,
        isAuditor,
        saveAuth,
        currentUser,
        setCurrentUser,
        logout,
        setisAdmin,
        setisPartner,
        setisFranchise,
        setisSponsor,
        setisAuditor,
        setisSupplyChainManager,
        setisFacilityManager,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

const AuthInit: FC<WithChildren> = ({children}) => {
  const {
    auth,
    logout,
    setCurrentUser,
    setisAdmin,
    setisAuditor,
    setisFranchise,
    setisPartner,
    setisSponsor,
    setisSupplyChainManager,
    setisFacilityManager,
  } = useAuth()
  const didRequest = useRef(false)
  const [showSplashScreen, setShowSplashScreen] = useState(true)
  // We should request user by authToken (IN OUR EXAMPLE IT'S API_TOKEN) before rendering the application
  useEffect(() => {
    const requestUser = async (apiToken: string) => {
      try {
        if (!didRequest.current) {
          const {data}: any = await getUserByToken(apiToken)
          if (data) {
            setCurrentUser(data)
            setisAdmin(data?.userType === 'ADMIN') // client
            setisPartner(data?.userType === 'CLIENT') //admin
            setisSponsor(data?.userType === 'SPONSOR') //sponsor
            setisFranchise(data?.userType === 'FRANCHISE') //franchise
            setisAuditor(data?.userType === 'AUDITOR') //auditor
            setisSupplyChainManager(data?.userType === 'FRANCHISE_USER') //supply chain/project manager
            setisFacilityManager(data?.userType === 'PICKUP_POINT_STAFF') //facility manager
          }
        }
      } catch (error) {
        console.error(error)
        if (!didRequest.current) {
          logout()
        }
      } finally {
        setShowSplashScreen(false)
      }

      return () => (didRequest.current = true)
    }
    if (auth && auth?.data?.userId) {
      requestUser(auth.data.userId)
    } else {
      logout()
      setShowSplashScreen(false)
    }
    // eslint-disable-next-line
  }, [])

  return showSplashScreen ? <LayoutSplashScreen /> : <>{children}</>
}

export {AuthProvider, AuthInit, useAuth}
